import { browser } from "$app/environment";
import { x25519 } from "@noble/curves/ed25519";
import { generateKeyPair } from "$crypto/pair-key";
import { b64uEncode, b64uDecode } from "$crypto/encryption";

/**
 * Per-device VuTwin identity.
 *
 * On first call we mint a fresh X25519 keypair. The 32-byte private scalar
 * is persisted to localStorage as a base64url string. The public point is
 * derived on demand and shared (via copy-paste / link) to bond with a friend.
 *
 * SECURITY NOTES (V1)
 * - localStorage is readable by any script on the same origin. Treat the
 *   private key as a "soft secret" — fine for a daily friendship game, not
 *   fine for a finance app.
 * - V2: move to IndexedDB-stored non-extractable WebCrypto keys.
 */

const STORAGE_KEY = "vutwin.identity.priv.v2";
const LEGACY_KEYS = ["vutwin.identity.priv.v1"];

class IdentityState {
  privateKey = $state<Uint8Array | null>(null);
  publicKey = $state<Uint8Array | null>(null);

  /** Base64url-encoded public key — the value you share with your friend. */
  publicKeyB64 = $derived.by(() =>
    this.publicKey ? b64uEncode(this.publicKey) : "",
  );

  /** Loads (or mints) the persistent device keypair. Idempotent. */
  ensureLoaded(): void {
    if (!browser) return;
    if (this.privateKey && this.publicKey) return;

    // Drop legacy entries from prior schemas so they don't squat storage.
    for (const k of LEGACY_KEYS) localStorage.removeItem(k);

    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        const priv = b64uDecode(stored);
        if (priv.length === 32) {
          this.privateKey = priv;
          this.publicKey = x25519.getPublicKey(priv);
          return;
        }
      } catch {
        // corrupted storage — fall through and regenerate
      }
    }

    const kp = generateKeyPair();
    this.privateKey = kp.privateKey;
    this.publicKey = kp.publicKey;
    localStorage.setItem(STORAGE_KEY, b64uEncode(kp.privateKey));
  }

  /** Wipe the device identity (debug / re-bond). */
  reset(): void {
    if (browser) {
      localStorage.removeItem(STORAGE_KEY);
      for (const k of LEGACY_KEYS) localStorage.removeItem(k);
    }
    this.privateKey = null;
    this.publicKey = null;
  }
}

export const identity = new IdentityState();
